import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import {
  learningHistoryApi,
  LearningActivityItem,
  LearningHistorySummary
} from '@/api/learningHistory'
import { learnerApi, LearnerClassCourse } from '@/api/b2b'
import {
  History,
  Video,
  Brain,
  FileEdit,
  CheckCircle,
  XCircle,
  Clock,
  Trophy,
  Target,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Filter
} from 'lucide-react'

// 활동 타입별 아이콘
function getActivityIcon(activityType: string) {
  switch (activityType) {
    case 'video_started':
    case 'video_completed':
      return Video
    case 'quiz_attempted':
    case 'quiz_passed':
    case 'quiz_completed':
      return Brain
    case 'assignment_submitted':
      return FileEdit
    default:
      return BookOpen
  }
}

// 활동 타입별 색상 스타일
function getActivityStyle(activityType: string) {
  switch (activityType) {
    case 'video_completed':
      return { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' }
    case 'video_started':
      return { bg: 'bg-blue-50', text: 'text-blue-500', border: 'border-blue-100' }
    case 'quiz_passed':
      return { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' }
    case 'quiz_completed':
    case 'quiz_attempted':
      return { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' }
    case 'assignment_submitted':
      return { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' }
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' }
  }
}

// 활동 타입 라벨 키
function getActivityLabelKey(activityType: string) {
  switch (activityType) {
    case 'video_started':
      return 'history.videoStarted'
    case 'video_completed':
      return 'history.videoCompleted'
    case 'quiz_attempted':
      return 'history.quizAttempted'
    case 'quiz_passed':
      return 'history.quizPassed'
    case 'quiz_completed':
      return 'history.quizCompleted'
    case 'assignment_submitted':
      return 'history.assignmentSubmitted'
    default:
      return 'history.learningActivity'
  }
}

// 날짜 포맷팅
function formatDate(dateString: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return t('history.today')
  } else if (diffDays === 1) {
    return t('history.yesterday')
  } else if (diffDays < 7) {
    return t('history.daysAgo', { days: diffDays })
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// 시간 포맷팅
function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 활동 그룹화 (날짜별)
function groupActivitiesByDate(activities: LearningActivityItem[]): Map<string, LearningActivityItem[]> {
  const groups = new Map<string, LearningActivityItem[]>()

  for (const activity of activities) {
    const date = new Date(activity.timestamp).toDateString()
    if (!groups.has(date)) {
      groups.set(date, [])
    }
    groups.get(date)!.push(activity)
  }

  return groups
}

// Summary Card Component
function SummaryCard({
  icon: Icon,
  label,
  value,
  subValue,
  color
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | number
  subValue?: string
  color: string
}) {
  return (
    <div className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${color}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-gray-50`}>
          <Icon className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    </div>
  )
}

// Activity Item Component
function ActivityItem({ activity }: { activity: LearningActivityItem }) {
  const Icon = getActivityIcon(activity.activityType)
  const style = getActivityStyle(activity.activityType)
  const { t } = useTranslation('learner')

  return (
    <div className={`flex gap-3 p-3 rounded-lg bg-white border ${style.border} hover:shadow-sm transition-shadow`}>
      <div className={`flex-shrink-0 w-10 h-10 rounded-full ${style.bg} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${style.text}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${style.text}`}>
            {t(getActivityLabelKey(activity.activityType))}
          </span>
          {activity.passed !== null && (
            activity.passed ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <XCircle className="w-4 h-4 text-red-400" />
            )
          )}
        </div>
        <div className="text-sm text-gray-700 font-medium truncate mt-0.5">
          <ContentMetadataText text={activity.lessonTitle || t('history.lesson')} />
        </div>
        {activity.moduleTitle && (
          <div className="text-xs text-gray-400 truncate">
            {activity.courseTitle && <><ContentMetadataText text={activity.courseTitle} /> &gt; </>}<ContentMetadataText text={activity.moduleTitle} />
          </div>
        )}
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTime(activity.timestamp)}
          </span>
          {activity.score !== null && (
            <span className="text-xs font-medium text-gray-600">
              {t('history.score', { score: activity.score })}
            </span>
          )}
          {activity.quizLocation && (
            <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
              {activity.quizLocation === 'pre' ? t('history.quizPre') : activity.quizLocation === 'post' ? t('history.quizPost') : t('history.quizCheckup')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default function LearningHistory() {
  const { t } = useTranslation('learner')
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const pageSize = 20

  const selectedClassProductId = searchParams.get('classProductId')
    ? parseInt(searchParams.get('classProductId')!)
    : undefined

  // 내 과정 목록 조회 (필터용)
  const { data: coursesData } = useQuery({
    queryKey: ['myClassCourses'],
    queryFn: () => learnerApi.getMyClassCourses(),
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지 (페이지 이동 시 재요청 방지)
    gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지
  })

  const courses = coursesData?.data?.data || []

  // 학습 기록 조회
  const { data: historyData, isLoading, error } = useQuery({
    queryKey: ['learningHistory', selectedClassProductId, page],
    queryFn: () => learningHistoryApi.getHistory({
      classProductId: selectedClassProductId,
      page,
      size: pageSize
    }),
  })

  const history = historyData?.data?.data
  const activities = history?.activities || []
  const summary = history?.summary
  const pagination = history?.pagination

  // 날짜별 그룹화
  const groupedActivities = groupActivitiesByDate(activities)

  // 필터 변경 핸들러
  const handleFilterChange = (classProductId: string) => {
    if (classProductId) {
      setSearchParams({ classProductId })
    } else {
      setSearchParams({})
    }
    setPage(0)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('history.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{t('history.loadError')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <History className="w-7 h-7 text-primary-500" />
            {t('history.title')}
          </h1>
          <p className="mt-1 text-gray-500">{t('history.subtitle')}</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard
            icon={BarChart3}
            label={t('history.totalActivities')}
            value={summary.totalActivities}
            color="border-primary-500"
          />
          <SummaryCard
            icon={Video}
            label={t('history.lessonsCompleted')}
            value={summary.lessonsCompleted}
            color="border-blue-500"
          />
          <SummaryCard
            icon={Target}
            label={t('history.quizAttempts')}
            value={summary.quizAttempts}
            subValue={t('history.quizAttemptsUnit') || undefined}
            color="border-amber-500"
          />
          <SummaryCard
            icon={Trophy}
            label={t('history.avgQuizScore')}
            value={summary.averageQuizScore !== null ? t('history.scoreUnit', { score: Math.round(summary.averageQuizScore) }) : '-'}
            color="border-green-500"
          />
        </div>
      )}

      {/* Filter */}
      {courses.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-gray-400" />
            <label className="text-sm font-medium text-gray-700">{t('history.courseFilter')}</label>
            <select
              value={selectedClassProductId || ''}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="flex-1 max-w-md px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t('history.allCourses')}</option>
              {courses.map((course: LearnerClassCourse) => (
                <option key={course.classProductId} value={course.classProductId}>
                  {course.className} - {course.contentTitle || course.productName}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Activity Timeline */}
      {activities.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <History className="w-16 h-16 text-gray-300 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">{t('history.noHistory')}</h3>
          <p className="mt-2 text-gray-500">{t('history.noHistoryDesc')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Array.from(groupedActivities.entries()).map(([dateKey, dateActivities]) => (
            <div key={dateKey}>
              <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {formatDate(dateActivities[0].timestamp, t)}
              </h3>
              <div className="space-y-2">
                {dateActivities.map((activity) => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('history.prev')}
          </button>
          <span className="text-sm text-gray-500">
            {t('history.page', { current: pagination.page + 1, total: pagination.totalPages })}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages - 1, p + 1))}
            disabled={page >= pagination.totalPages - 1}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('history.next')}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
